// src/pages/AISentry.jsx
// AI-Enhanced Network Topology - Proactive Protection
// Powered by AI Sentry - With Nimbus Integration for Autonomous Actions

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ReactMarkdown from 'react-markdown';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';
import { assetsService } from '../services/assets';
import api from '../services/api';
import { eventSourceLabel } from '../utils/eventSource';
import { buildShortBriefingPrompt, buildExpandedBriefingPrompt, SITE_BRIEFING_MODE } from '../utils/briefingPrompt';
import { deviceTypeLabel, deviceRoleClass, knownDeviceName } from '../constants/deviceTypes';
import { eventEnforcementOff } from '../utils/monitorMode';
import { detectFindings, computeCounts, isAlert, siteEvents, siteThreats, isFinding, EVENT_SAMPLE_LIMIT } from '../utils/counts';
import EnforcementOffLabel from '../components/common/EnforcementOffLabel';
import { edgeNodeDisplayName } from '../utils/edgeNodeDisplayName';
import useCortexEvents from '../hooks/useCortexEvents';

// ════════════════════════════════════════════════════════════════════════════
// MARKDOWN STYLING COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const MarkdownComponents = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-white mb-3 mt-4 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-cyan-300 mb-2 mt-4 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-cyan-200 mb-2 mt-3">{children}</h3>,
  p: ({ children }) => <p className="text-slate-200 text-sm mb-2 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc list-inside text-slate-200 text-sm mb-2 space-y-1 ml-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-slate-200 text-sm mb-2 space-y-1 ml-2">{children}</ol>,
  li: ({ children }) => <li className="text-slate-200">{children}</li>,
  hr: () => <hr className="border-slate-600 my-3" />,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3 rounded-lg border border-slate-600">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-700/50">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-slate-600">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-slate-700/30">{children}</tr>,
  th: ({ children }) => <th className="px-2 py-1.5 text-left font-semibold text-cyan-300 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1.5 text-slate-200 whitespace-nowrap">{children}</td>,
  code: ({ children }) => <code className="bg-slate-700 px-1 py-0.5 rounded text-cyan-300 text-xs font-mono">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-cyan-500 pl-3 my-2 text-slate-300 italic">{children}</blockquote>,
};

// ════════════════════════════════════════════════════════════════════════════
// KNOWN DEVICE REGISTRY
// lab-dc-prime (192.168.1.x): legacy factory PLCs
// site-atlanta/frankfurt/singapore (10.x.1.x): substation IEDs
// Priority: assets API > this registry > event.details.dest_role > 'Device'
// ════════════════════════════════════════════════════════════════════════════

const KNOWN_DEVICES = {
  // lab-dc-prime — legacy OT sandbox factory PLCs (192.168.1.70–75)
  '192.168.1.70': { name: 'Mitsubishi FX5U', role: 'PLC',     vendor: 'Mitsubishi' },
  '192.168.1.71': { name: 'Omron NX1P2',     role: 'PLC',     vendor: 'Omron' },
  '192.168.1.72': { name: 'Beckhoff CX5130', role: 'PLC',     vendor: 'Beckhoff' },
  '192.168.1.73': { name: 'ABB PM5650',      role: 'PLC',     vendor: 'ABB' },
  '192.168.1.75': { name: 'Ignition HMI',    role: 'HMI',     vendor: 'Inductive Automation' },
  // site-atlanta (10.10.1.x) — substation IEDs
  '10.10.1.70': { name: 'SEL-411L Line Relay',  role: 'IED',     vendor: 'SEL' },
  '10.10.1.71': { name: 'SEL-751 Feeder Relay', role: 'IED',     vendor: 'SEL' },
  '10.10.1.72': { name: 'GE L90 Line Diff',     role: 'IED',     vendor: 'GE' },
  '10.10.1.73': { name: 'ABB REL670 Distance',  role: 'IED',     vendor: 'ABB' },
  '10.10.1.74': { name: 'Siemens SICAM RTU',    role: 'RTU',     vendor: 'Siemens' },
  '10.10.1.75': { name: 'IEC 61850 Gateway',    role: 'Gateway', vendor: 'Moxa' },
  '10.10.1.76': { name: 'SCADA Master',         role: 'SCADA',   vendor: 'OSIsoft' },
  // site-frankfurt (10.20.1.x) — substation IEDs
  '10.20.1.70': { name: 'SEL-411L Line Relay',  role: 'IED',     vendor: 'SEL' },
  '10.20.1.71': { name: 'SEL-751 Feeder Relay', role: 'IED',     vendor: 'SEL' },
  '10.20.1.72': { name: 'GE L90 Line Diff',     role: 'IED',     vendor: 'GE' },
  '10.20.1.73': { name: 'ABB REL670 Distance',  role: 'IED',     vendor: 'ABB' },
  '10.20.1.74': { name: 'Siemens SICAM RTU',    role: 'RTU',     vendor: 'Siemens' },
  '10.20.1.75': { name: 'IEC 61850 Gateway',    role: 'Gateway', vendor: 'Moxa' },
  '10.20.1.76': { name: 'SCADA Master',         role: 'SCADA',   vendor: 'OSIsoft' },
  // site-singapore (10.30.1.x) — substation IEDs
  '10.30.1.70': { name: 'SEL-411L Line Relay',  role: 'IED',     vendor: 'SEL' },
  '10.30.1.71': { name: 'SEL-751 Feeder Relay', role: 'IED',     vendor: 'SEL' },
  '10.30.1.72': { name: 'GE L90 Line Diff',     role: 'IED',     vendor: 'GE' },
  '10.30.1.73': { name: 'ABB REL670 Distance',  role: 'IED',     vendor: 'ABB' },
  '10.30.1.74': { name: 'Siemens SICAM RTU',    role: 'RTU',     vendor: 'Siemens' },
  '10.30.1.75': { name: 'IEC 61850 Gateway',    role: 'Gateway', vendor: 'Moxa' },
  '10.30.1.76': { name: 'SCADA Master',         role: 'SCADA',   vendor: 'OSIsoft' },
};

const ROLE_STYLES = {
  PLC:     { bg: 'from-orange-500/20 to-amber-600/20',   border: 'border-orange-400/60',   badge: 'bg-orange-500/30 text-orange-100 border-orange-400/50',   text: 'text-orange-300' },
  HMI:     { bg: 'from-blue-500/20 to-cyan-600/20',      border: 'border-blue-400/60',     badge: 'bg-blue-500/30 text-blue-100 border-blue-400/50',         text: 'text-blue-300' },
  RTU:     { bg: 'from-emerald-500/20 to-green-600/20',  border: 'border-emerald-400/60',  badge: 'bg-emerald-500/30 text-emerald-100 border-emerald-400/50', text: 'text-emerald-300' },
  IED:     { bg: 'from-purple-500/20 to-violet-600/20',  border: 'border-purple-400/60',   badge: 'bg-purple-500/30 text-purple-100 border-purple-400/50',   text: 'text-purple-300' },
  Relay:   { bg: 'from-teal-500/20 to-cyan-600/20',      border: 'border-teal-400/60',     badge: 'bg-teal-500/30 text-teal-100 border-teal-400/50',         text: 'text-teal-300' },
  SCADA:   { bg: 'from-sky-500/20 to-blue-600/20',       border: 'border-sky-400/60',      badge: 'bg-sky-500/30 text-sky-100 border-sky-400/50',            text: 'text-sky-300' },
  Gateway: { bg: 'from-indigo-500/20 to-slate-600/20',   border: 'border-indigo-400/60',   badge: 'bg-indigo-500/30 text-indigo-100 border-indigo-400/50',   text: 'text-indigo-300' },
  SCADAMaster:      { bg: 'from-sky-500/20 to-blue-600/20',  border: 'border-sky-400/60',  badge: 'bg-sky-500/30 text-sky-100 border-sky-400/50',  text: 'text-sky-300' },
  SubstationGateway:{ bg: 'from-indigo-500/20 to-slate-600/20', border: 'border-indigo-400/60', badge: 'bg-indigo-500/30 text-indigo-100 border-indigo-400/50', text: 'text-indigo-300' },
  ProtectionRelay:  { bg: 'from-teal-500/20 to-cyan-600/20',      border: 'border-teal-400/60',   badge: 'bg-teal-500/30 text-teal-100 border-teal-400/50',     text: 'text-teal-300' },
  Historian:        { bg: 'from-lime-500/20 to-green-600/20',     border: 'border-lime-400/60',   badge: 'bg-lime-500/30 text-lime-100 border-lime-400/50',     text: 'text-lime-300' },
};
const ROLE_STYLE_DEFAULT = { bg: 'from-slate-500/20 to-gray-600/20', border: 'border-slate-400/60', badge: 'bg-slate-500/30 text-slate-200 border-slate-400/50', text: 'text-slate-300' };

const RoleIcon = ({ role, className = 'w-5 h-5' }) => {
  if (role === 'HMI' || role === 'SCADA' || role === 'SCADAMaster')
    return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
  if (role === 'RTU')
    return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" /></svg>;
  if (role === 'IED')
    return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
  if (role === 'Relay' || role === 'ProtectionRelay')
    return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
  if (role === 'Historian')
    return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7c0-1.657 3.582-3 8-3s8 1.343 8 3-3.582 3-8 3-8-1.343-8-3zm0 0v10c0 1.657 3.582 3 8 3s8-1.343 8-3V7M4 12c0 1.657 3.582 3 8 3s8-1.343 8-3" /></svg>;
  if (role === 'Gateway' || role === 'SubstationGateway')
    return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>;
  // PLC / default — circuit board icon
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>;
};

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  // ISOLATED only when an event's real action isolated the host (monitor mode never does).
  ISOLATED: { key: 'ISOLATED', icon: '🛡️', text: 'ISOLATED', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50' },
  // Source of a critical unauthorized-access finding; no action was taken on it.
  CRITICAL: { key: 'CRITICAL', icon: '🚨', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50' },
  ANALYZING: { key: 'ANALYZING', icon: '🔍', text: 'ANALYZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', pulse: true },
  ALERT: { key: 'ALERT', icon: '⚠️', text: 'ALERT', bg: 'bg-yellow-500', glow: 'from-yellow-500/50 to-amber-500/50' },
  PROTECTED: { key: 'PROTECTED', icon: '🤖', text: 'MONITORED', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30' },
};

const isRedStatus = (s) => s === AI_STATUS.ISOLATED || s === AI_STATUS.CRITICAL;

// ════════════════════════════════════════════════════════════════════════════
// NIMBUS API INTEGRATION - Execute actions on Stratus routers
// ════════════════════════════════════════════════════════════════════════════

const executeNimbusAction = async (actionType, params) => {
  try {
    const response = await api.post('/nimbus/action/', {
      action_type: actionType,
      params: params
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Nimbus action failed:', error);
    return { success: false, error: error.message };
  }
};

// ════════════════════════════════════════════════════════════════════════════
// HEURISTIC DETECTION (for visual badges) + VPN RECOMMENDATIONS
// ════════════════════════════════════════════════════════════════════════════

// Findings are correlated in one place (utils/counts.js); this name is kept for the tests.
const detectThreatsHeuristic = detectFindings;

const getDeviceAIStatus = (deviceIp, threats) => {
  const asSource = threats.find(t => t.device === deviceIp);
  if (asSource) {
    if (asSource.type === 'ISOLATION') return asSource.isolated ? AI_STATUS.ISOLATED : AI_STATUS.CRITICAL;
    if (asSource.type === 'RECOMMENDATION') return AI_STATUS.ALERT;
  }
  const asTarget = threats.find(t => t.targetDevices?.includes(deviceIp));
  if (asTarget && asTarget.type === 'ISOLATION') return AI_STATUS.ANALYZING;
  return AI_STATUS.PROTECTED;
};

// ════════════════════════════════════════════════════════════════════════════
// AI Sentry API CALLS
// ════════════════════════════════════════════════════════════════════════════

// Briefing prompts: utils/briefingPrompt.js (WP11 tone; one builder for both pages). The reply is
// shown as plain text (short) and markdown (expanded); a reply carrying the backend's error text
// falls back to the static summary.
const requestBriefing = async (prompt, sessionPrefix, label) => {
  try {
    const response = await api.post('/chat/', {
      message: prompt,
      history: [],
      session_id: `${sessionPrefix}-${Date.now()}`,
      mode: SITE_BRIEFING_MODE,
    });
    const content = response.data.content;
    if (content && content.includes('encountered an error')) return null;
    return content || null;
  } catch (error) {
    console.log(`${label} error:`, error.message);
    return null;
  }
};

// counts: the screen's computeCounts for the same site and events (its findingList feeds the opening).
const generateAIBriefing = (siteId, siteEvents, counts) =>
  requestBriefing(buildShortBriefingPrompt(siteId, siteEvents, counts), 'briefing', 'AI briefing');

const generateExpandedBriefing = (siteId, siteEvents, counts) =>
  requestBriefing(buildExpandedBriefingPrompt(siteId, siteEvents, counts), 'expanded', 'Expanded briefing');

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

// Check if node is online based on heartbeat (15 min threshold)
// Uses UTC comparison to work correctly across all timezones
const isNodeOnline = (node) => {
  if (!node.last_heartbeat) return false;
  
  // Ensure timestamp is parsed as UTC
  let heartbeatStr = node.last_heartbeat;
  if (!heartbeatStr.endsWith('Z') && !heartbeatStr.includes('+')) {
    heartbeatStr += 'Z';  // Treat as UTC if no timezone specified
  }
  
  const lastHeartbeat = new Date(heartbeatStr).getTime();
  const nowUTC = Date.now();  // Always returns UTC milliseconds
  const diffMinutes = (nowUTC - lastHeartbeat) / (1000 * 60);
  
  return diffMinutes <= 15;  // 15 minute threshold
};

// Calculate status from heartbeat (not from backend status field)
const getNodeStatus = (node) => {
  return isNodeOnline(node) ? 'Online' : 'Offline';
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const CloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-purple-400/30 rounded-2xl blur-xl opacity-75" />
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[200px] shadow-2xl">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400" />
      </div>
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <svg className="w-8 h-8 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      <div className="text-center">
        <div className="text-sm font-semibold text-white">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-600/50 flex justify-center gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.edgeCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase">Agents</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase">Devices</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] text-emerald-300 font-medium">EdgeNotary Active</span>
      </div>
    </div>
  </div>
);

const EdgeGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[220px] shadow-xl transition-all group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'}`} />
        </div>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.displayName || data.nodeId}</div>
          </div>
        </div>
        <div className="mt-3 pt-2 border-t border-slate-500/30 flex justify-between text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Devices</span>
            <div className="font-semibold text-blue-300">{data.deviceCount || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PROTOCOL_COLORS = {
  modbus:    { badge: 'bg-orange-500/30 text-orange-200 border-orange-400/50' },
  opcua:     { badge: 'bg-purple-500/30 text-purple-200 border-purple-400/50' },
  dnp3:      { badge: 'bg-emerald-500/30 text-emerald-200 border-emerald-400/50' },
  mms:       { badge: 'bg-violet-500/30 text-violet-200 border-violet-400/50' },
  iec61850:  { badge: 'bg-indigo-500/30 text-indigo-200 border-indigo-400/50' },
  goose:     { badge: 'bg-amber-500/30 text-amber-200 border-amber-400/50' },
  snmp:      { badge: 'bg-gray-500/30 text-gray-200 border-gray-400/50' },
  bacnet:    { badge: 'bg-teal-500/30 text-teal-200 border-teal-400/50' },
  tcp:       { badge: 'bg-blue-500/30 text-blue-200 border-blue-400/50' },
  s7:        { badge: 'bg-cyan-500/30 text-cyan-200 border-cyan-400/50' },
  default:   { badge: 'bg-slate-500/30 text-slate-200 border-slate-400/50' },
};

const DeviceNode = ({ data }) => {
  const aiStatus = data.aiStatus || AI_STATUS.PROTECTED;
  const hasViolations = data.policyViolations > 0;
  const role = data.role || 'Device';
  const roleStyle = ROLE_STYLES[role] || ROLE_STYLE_DEFAULT;

  const getBorderClass = () => {
    if (isRedStatus(aiStatus)) return 'border-red-500 border-2';
    if (aiStatus === AI_STATUS.ANALYZING) return 'border-purple-500 border-2';
    if (aiStatus === AI_STATUS.ALERT) return 'border-yellow-500 border-2';
    if (hasViolations) return 'border-red-400 border';
    return `${roleStyle.border} border`;
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-orange-400 !border-2 !border-white" />

      {aiStatus !== AI_STATUS.PROTECTED && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}

      <div className={`relative bg-gradient-to-br from-slate-700 to-slate-800 ${getBorderClass()} rounded-lg p-3 min-w-[185px] max-w-[200px] shadow-lg transition-all group-hover:scale-105 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>

        {/* AI status badge */}
        <div className={`absolute -top-2 -right-2 px-1.5 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>

        {/* Header row: role icon + device name + role badge */}
        <div className="flex items-start gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${roleStyle.bg} border ${roleStyle.border}`}>
            <RoleIcon role={role} className={`w-4 h-4 ${roleStyle.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-white truncate leading-tight">
              {data.name || data.ip}
            </div>
            {data.vendor && (
              <div className="text-[9px] text-slate-400 truncate">{data.vendor}</div>
            )}
          </div>
          <span className={`text-[8px] px-1.5 py-0.5 rounded border font-bold uppercase flex-shrink-0 max-w-[84px] text-right leading-tight ${roleStyle.badge}`}>
            {data.typeLabel || role}
          </span>
        </div>

        {/* IP address */}
        <div className="bg-slate-900/60 rounded px-2 py-1 mb-2">
          <div className={`text-[11px] font-mono font-semibold ${
            isRedStatus(aiStatus) ? 'text-red-300' :
            aiStatus === AI_STATUS.ALERT ? 'text-yellow-300' : 'text-cyan-300'
          }`}>
            {data.ip}
          </div>
        </div>

        {/* Protocol badges */}
        {data.protocols && data.protocols.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {data.protocols.map((protocol, idx) => {
              const pc = PROTOCOL_COLORS[protocol] || PROTOCOL_COLORS.default;
              return (
                <span key={idx} className={`text-[8px] px-1.5 py-0.5 rounded border font-semibold uppercase ${pc.badge}`}>
                  {protocol}
                </span>
              );
            })}
          </div>
        )}

        {/* Footer: event count + violation indicator */}
        <div className="flex items-center justify-between text-[9px] text-slate-400">
          {/* Counts over the window the top bar states (utils/counts.js), not 24h. */}
          <span>{data.eventCount || 0} events</span>
          {hasViolations && (
            <span className="text-red-400 font-semibold">{data.policyViolations} alerts</span>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { cloud: CloudNode, edgeGateway: EdgeGatewayNode, device: DeviceNode };

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING MODAL
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingModal = ({ isOpen, onClose, aiBriefing, stats, threats, counts, siteName, isLoadingAI, onRequestExpanded, expandedBriefing, isLoadingExpanded }) => {
  const [showExpanded, setShowExpanded] = useState(false);
  // The modal stays mounted while closed, so the expanded section would reopen expanded with no
  // request in flight ("Unable to generate expanded analysis."). Collapse it on every close.
  useEffect(() => { if (!isOpen) setShowExpanded(false); }, [isOpen]);
  
  if (!isOpen) return null;
  
  // Findings and alerts: the same numbers, from the same window, as the top bar (utils/counts.js).
  const findingList = counts?.findingList || threats.filter(isFinding);
  const findingCount = counts ? counts.findings : findingList.length;
  const alertCount = counts ? counts.alerts : (stats.alertCount || 0);
  const isolatedCount = findingList.filter(t => t.type === 'ISOLATION').length;
  const hasCritical = isolatedCount > 0;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-600 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${hasCritical ? 'from-red-600/20 to-orange-600/20' : 'from-cyan-600/20 to-blue-600/20'} border-b border-slate-700 px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${hasCritical ? 'bg-red-500/30' : 'bg-cyan-500/30'} rounded-xl flex items-center justify-center`}>
              <span className="text-2xl">{hasCritical ? '🚨' : '🤖'}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Briefing</h2>
              <p className="text-slate-400 text-sm">EdgeNotary Security Summary</p>
              <p className="text-slate-500 text-xs italic">Interpretation — generated, not evidence.</p>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-300">{stats.totalDevices || 0}</div>
              <div className="text-xs text-slate-400">Devices</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <div data-testid="briefing-findings" className="text-2xl font-bold text-red-300">{findingCount}</div>
              <div className="text-xs text-slate-400">Findings</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <div data-testid="briefing-alerts" className="text-2xl font-bold text-blue-300">{alertCount}</div>
              <div className="text-xs text-slate-400">Alerts</div>
            </div>
          </div>
          {counts?.window && (
            <p data-testid="briefing-window" className="text-xs text-slate-400 -mt-2">
              {siteName ? `${siteName} · ` : ''}{counts.window.label}
            </p>
          )}
          
          {/* AI Briefing */}
          <div className={`rounded-xl p-4 ${hasCritical ? 'bg-red-900/20 border border-red-500/30' : 'bg-slate-700/30'}`}>
            {isLoadingAI ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                <span className="text-slate-300 text-sm">EdgeNotary is analyzing your network...</span>
              </div>
            ) : aiBriefing ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🧠</span>
                  <span className="text-sm font-semibold text-cyan-300">Operator Summary</span>
                </div>
                <p className="text-white text-sm leading-relaxed">{aiBriefing}</p>
              </>
            ) : (
              <>
                <p className="text-white font-medium mb-2">
                  {hasCritical ? '⚠️ Attention needed:' : '✅ Status:'}
                </p>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {hasCritical 
                    ? `Detected ${isolatedCount} threat source${isolatedCount > 1 ? 's' : ''} attempting unauthorized access.`
                    : `All ${stats.totalDevices || 0} devices operating normally.`
                  }
                </p>
              </>
            )}
          </div>
          
          {/* Expanded Briefing Toggle */}
          {aiBriefing && (
            <div>
              {!showExpanded ? (
                <button
                  onClick={() => {
                    setShowExpanded(true);
                    if (!expandedBriefing) onRequestExpanded();
                  }}
                  className="w-full py-2 text-sm text-cyan-300 hover:text-cyan-200 flex items-center justify-center gap-2 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show Expanded Analysis
                </button>
              ) : (
                <div className="bg-slate-700/30 rounded-xl p-4 border border-cyan-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📊</span>
                      <span className="text-sm font-semibold text-cyan-300">Expanded Analysis</span>
                    </div>
                    <button onClick={() => setShowExpanded(false)} className="text-slate-400 hover:text-white text-xs">
                      Collapse ▲
                    </button>
                  </div>
                  {isLoadingExpanded ? (
                    <div className="flex items-center gap-3 py-4">
                      <div className="w-5 h-5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                      <span className="text-slate-300 text-sm">Generating detailed analysis...</span>
                    </div>
                  ) : expandedBriefing ? (
                    <div className="max-h-80 overflow-y-auto pr-2 prose-sm">
                      <ReactMarkdown components={MarkdownComponents}>
                        {expandedBriefing}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">Unable to generate expanded analysis.</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Finding list — every finding the Findings tile counts; no cap, no clipping scroll box. */}
          {findingList.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-400 mb-2">Detected Findings ({findingList.length})</h3>
              <div data-testid="briefing-finding-list" className="space-y-2">
                {findingList.map(threat => (
                  <div key={threat.id} className={`flex items-center gap-3 rounded-lg p-2 border ${threat.type === 'ISOLATION' ? 'bg-red-900/20 border-red-700/30' : 'bg-amber-900/20 border-amber-700/30'}`}>
                    <span className="text-lg">{threat.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{threat.reason}</div>
                      <div className="text-xs text-slate-400">{threat.device} → {threat.targetDevices?.length || 0} target(s)</div>
                    </div>
                    <div className={`text-xs ${threat.type === 'ISOLATION' ? 'text-red-400' : 'text-amber-400'}`}>{threat.confidence}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-slate-800/50 border-t border-slate-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-md flex items-center justify-center">
              <span className="text-xs font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-400">Powered by <span className="text-cyan-400 font-semibold">EdgeNotary</span></span>
          </div>
          <button onClick={onClose} className={`px-6 py-2 ${hasCritical ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500' : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500'} text-white font-semibold rounded-lg transition-all`}>
            {hasCritical ? 'Review Findings' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// AI PANEL - Shows heuristic detections, VPN actions, and recent events
// ════════════════════════════════════════════════════════════════════════════

const AIPanel = ({ isOpen, onClose, threats, recentEvents, counts, edgeNodes = [], onRefresh, isRefreshing, onApproveVPN, selectedAgent }) => {
  const [activeTab, setActiveTab] = useState('detections');
  const [executingAction, setExecutingAction] = useState(null);
  const [actionResults, setActionResults] = useState({});
  const cortexEvents = useCortexEvents(selectedAgent);

  if (!isOpen) return null;

  // Every count in the panel comes from utils/counts.js over the window the header states.
  const c = counts || computeCounts({ events: recentEvents, threats });
  const allCritical = recentEvents?.filter(e => e.severity === 'Critical') || [];
  const allWarning = recentEvents?.filter(e => e.severity === 'Warning') || [];
  const criticalEvents = allCritical.slice(0, 10);
  const warningEvents = allWarning.slice(0, 10);
  // A site is named by its display name, never its raw id.
  const siteName = (id) => (id ? edgeNodeDisplayName(edgeNodes.find(n => n.edge_node_id === id) || { edge_node_id: id }) : '—');
  
  // Separate VPN actions from other threats
  const vpnActions = threats.filter(t => t.type === 'VPN_ACTION');
  const otherThreats = c.findingList;
  
  const handleApproveVPN = async (action) => {
    setExecutingAction(action.id);
    try {
      const result = await onApproveVPN(action);
      setActionResults(prev => ({ ...prev, [action.id]: result }));
    } catch (error) {
      setActionResults(prev => ({ ...prev, [action.id]: { success: false, error: error.message } }));
    } finally {
      setExecutingAction(null);
    }
  };
  
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[686px] bg-slate-900 border-l border-slate-700 z-50 shadow-2xl overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl">🤖</span>
            <div>
              <h2 className="text-white font-bold">EdgeNotary Activity</h2>
              <p data-testid="panel-counts" className="text-slate-400 text-xs">{c.findings} detections • {c.alerts} alerts • {c.events} events</p>
              {c.window && <p data-testid="panel-window" className="text-slate-500 text-[10px]">{c.window.label}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">✕</button>
          </div>
        </div>
        
        {/* Status Bar */}
        <div className="px-6 py-3 bg-emerald-500/10 border-b border-emerald-500/30 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-300 text-sm font-medium">Monitoring Active</span>
            </div>
            <span className="text-slate-500 text-xs">Observes. Never acts.</span>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-slate-700 flex-shrink-0">
          <button
            onClick={() => setActiveTab('detections')}
            className={`flex-1 px-2 py-3 text-xs font-medium transition-colors ${
              activeTab === 'detections'
                ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            🛡️ Detections ({otherThreats.length})
          </button>
          <button
            onClick={() => setActiveTab('actions')}
            className={`flex-1 px-2 py-3 text-xs font-medium transition-colors ${
              activeTab === 'actions'
                ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            ⚡ Actions ({vpnActions.length})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 px-2 py-3 text-xs font-medium transition-colors ${
              activeTab === 'events'
                ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            📊 Events
          </button>
          <button
            onClick={() => setActiveTab('cortex')}
            className={`flex-1 px-2 py-3 text-xs font-medium transition-colors ${
              activeTab === 'cortex'
                ? 'text-amber-300 border-b-2 border-amber-400 bg-amber-500/10'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            🔗 CORTEX{cortexEvents.length > 0 && ` (${cortexEvents.length})`}
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'detections' ? (
            // Heuristic Detections Tab
            otherThreats.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">✅</span>
                <p className="text-slate-300 font-medium mt-4">All Clear</p>
                <p className="text-slate-500 text-sm mt-1">No threat patterns detected</p>
              </div>
            ) : (
              otherThreats.map(threat => (
                <div key={threat.id} className={`border-2 rounded-xl p-4 ${
                  threat.type === 'ISOLATION' ? 'border-red-500 bg-red-500/10' : 'border-yellow-500 bg-yellow-500/10'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{threat.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
                      <div>
                        <div className="text-white font-semibold text-sm">{threat.title || threat.type}</div>
                        <div className="text-slate-400 text-xs">Heuristic Pattern</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-400 text-[10px]">Confidence</div>
                      <div className="text-white font-bold">{threat.confidence}%</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="font-mono text-sm bg-slate-800 px-2 py-1 rounded text-cyan-300">{threat.device}</span>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{threat.reason}</div>
                    <div className="text-slate-400 text-xs mt-1">
                      Targeting: {threat.targetDevices?.slice(0, 3).join(', ')}{threat.targetDevices?.length > 3 ? ` +${threat.targetDevices.length - 3} more` : ''}
                    </div>
                  </div>
                </div>
              ))
            )
          ) : activeTab === 'actions' ? (
            // Network Response Actions Tab
            vpnActions.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">⚡</span>
                <p className="text-slate-300 font-medium mt-4">No Pending Actions</p>
                <p className="text-slate-500 text-sm mt-1">EdgeNotary will recommend actions when threats are detected</p>
              </div>
            ) : (
              vpnActions.map(action => {
                const result = actionResults[action.id];
                const isExecuting = executingAction === action.id;
                
                return (
                  <div key={action.id} className="border-2 border-purple-500 bg-purple-500/10 rounded-xl p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔒</span>
                        <div>
                          <div className="text-white font-semibold text-sm">Evaluation</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-slate-400 text-[10px]">Confidence</div>
                        <div className="text-white font-bold">{action.confidence}%</div>
                      </div>
                    </div>
                    
                    {/* Target Router */}
                    <div className="mb-3 p-2 bg-slate-800/50 rounded-lg">
                      <div className="text-[10px] text-slate-400 uppercase mb-1">Target Router</div>
                      <div className="font-mono text-sm text-cyan-300">{siteName(action.agentId || selectedAgent)}</div>
                    </div>
                    
                    {/* Action Details - Tag Assignment */}
                    <div data-testid="suggested-action" className="mb-3 p-3 bg-slate-800/80 rounded-lg border border-purple-500/30">
                      <div className="bg-slate-900/50 rounded px-3 py-2 font-mono text-sm text-purple-300">
                        aisentry-isolated-{new Date().toISOString().slice(0,10).replace(/-/g,'')}
                      </div>
                      <div className="mt-1.5 text-[10px] text-slate-400">Suggested router tag — not applied (monitor mode)</div>
                    </div>
                    
                    {/* Threat Info */}
                    <div className="mb-4 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="text-xs text-red-300 font-medium mb-1">For finding:</div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-red-400">{action.device}</span>
                        <span className="text-slate-500">→</span>
                        <span className="text-slate-300 text-sm">{action.targetDevices?.length || 0} devices targeted</span>
                      </div>
                    </div>
                    
                    {/* Result Display */}
                    {result && (
                      <div className={`mb-3 p-3 rounded-lg ${result.success ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{result.success ? '✅' : '❌'}</span>
                          <div>
                            <div className={`text-sm font-medium ${result.success ? 'text-emerald-300' : 'text-red-300'}`}>
                              {result.success ? 'Tag Applied Successfully!' : `Failed: ${result.error}`}
                            </div>
                            {result.success && result.data?.tag_name && (
                              <div className="text-emerald-200/70 text-xs mt-0.5 font-mono">{result.data.tag_name}</div>
                            )}
                          </div>
                        </div>
                        {result.success && (
                          <p className="text-emerald-200/70 text-xs mt-2">✨ Tag should appear on the router</p>
                        )}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    {!result?.success && (
                      <div className="flex gap-2">
                        <button
                          disabled
                          title="Not yet wired."
                          className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                        >
                          <span>✓</span>
                          Accept as anomalous — open evaluation
                        </button>
                        <button
                          disabled={isExecuting}
                          className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 text-sm font-medium rounded-lg transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )
          ) : activeTab === 'events' ? (
            // Events Tab
            <>
              {/* Critical Events */}
              {criticalEvents.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    Critical Events ({allCritical.length > criticalEvents.length ? `showing ${criticalEvents.length} of ${allCritical.length}` : allCritical.length})
                  </h3>
                  <div className="space-y-2">
                    {criticalEvents.map((event, idx) => (
                      <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="flex items-center gap-1.5 flex-wrap"><span className="text-xs text-red-300 font-medium">{event.details?.policy_name || 'Policy Match'}</span>{eventEnforcementOff(event) && <EnforcementOffLabel variant="dark" />}</span>
                          <span className="text-[10px] text-slate-500">
                            {event.timestamp ? new Date(event.timestamp + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-300">{eventSourceLabel(event)}</span>
                          <span className="text-slate-500">→</span>
                          <span className="font-mono text-cyan-300">{event.destination_ip || 'N/A'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1 truncate">
                          {event.description || event.details?.rule_name || 'Security event detected'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Warning Events */}
              {warningEvents.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                    Warning Events ({allWarning.length > warningEvents.length ? `showing ${warningEvents.length} of ${allWarning.length}` : allWarning.length})
                  </h3>
                  <div className="space-y-2">
                    {warningEvents.map((event, idx) => (
                      <div key={idx} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="flex items-center gap-1.5 flex-wrap"><span className="text-xs text-yellow-300 font-medium">{event.details?.policy_name || 'Policy Match'}</span>{eventEnforcementOff(event) && <EnforcementOffLabel variant="dark" />}</span>
                          <span className="text-[10px] text-slate-500">
                            {event.timestamp ? new Date(event.timestamp + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-300">{eventSourceLabel(event)}</span>
                          <span className="text-slate-500">→</span>
                          <span className="font-mono text-cyan-300">{event.destination_ip || 'N/A'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1 truncate">
                          {event.description || event.details?.rule_name || 'Security event detected'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* No Events */}
              {criticalEvents.length === 0 && warningEvents.length === 0 && (
                <div className="text-center py-12">
                  <span className="text-3xl">📭</span>
                  <p className="text-slate-300 font-medium mt-4">No Recent Alerts</p>
                  <p className="text-slate-500 text-sm mt-1">Click refresh to fetch latest events</p>
                </div>
              )}
            </>
          ) : activeTab === 'cortex' ? (
            // CORTEX API Tab
            cortexEvents.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-3xl mb-3">🔗</div>
                <p className="text-slate-300 font-medium">No CORTEX Events</p>
                <p className="text-slate-500 text-sm mt-1">Observes. Never acts. · incidents appear as policies fire</p>
              </div>
            ) : (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  XSOAR Incidents ({cortexEvents.length})
                </h3>
                {cortexEvents.map(ev => {
                  const sevStyle = { 1: 'bg-red-500/20 text-red-400', 2: 'bg-orange-500/20 text-orange-400', 3: 'bg-yellow-500/20 text-yellow-400', 4: 'bg-slate-700 text-slate-400' };
                  const sevLabel = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };
                  return (
                    <div key={ev.id} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                      <div className="flex items-start justify-between mb-1">
                        <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">{ev.id}</span>
                        <span className="text-[10px] text-slate-500">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false}) : ''}
                        </span>
                      </div>
                      <div className="text-xs text-slate-200 font-medium mb-1 truncate" title={ev.name}>{ev.name}</div>
                      <div className="flex items-center gap-2 text-xs mb-1.5">
                        <span className="font-mono text-slate-300">{ev.source_ip || '—'}</span>
                        <span className="text-slate-500">→</span>
                        <span className="font-mono text-cyan-300">{ev.destination_ip || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${sevStyle[ev.severity] || sevStyle[4]}`}>{sevLabel[ev.severity] || 'Info'}</span>
                        {ev.protocol && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">{ev.protocol}</span>}
                        {ev.edge_node && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/80 text-slate-400 max-w-[100px] truncate">{siteName(ev.edge_node)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : null}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-cyan-400 to-blue-500 rounded flex items-center justify-center">
              <span className="text-[10px] font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-500">Powered by <span className="text-cyan-400">EdgeNotary</span></span>
          </div>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// EDGE NODE FILTER
// ════════════════════════════════════════════════════════════════════════════

const EdgeNodeFilter = ({ edgeNodes, selectedAgent, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedNode = selectedAgent ? edgeNodes.find(n => n.edge_node_id === selectedAgent) : null;
  
  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 bg-slate-700/80 hover:bg-slate-600/80 border border-slate-500/50 rounded-lg px-4 py-2 text-sm text-white min-w-[280px]">
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="flex-1 text-left truncate">{selectedNode ? edgeNodeDisplayName(selectedNode) : 'All Edge Nodes'}</span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-slate-800 border border-slate-600/50 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
            <button onClick={() => { onSelect(null); setIsOpen(false); }} className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-700/50 ${!selectedAgent ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-200'}`}>
              All Edge Nodes
            </button>
            {[...edgeNodes].sort((a, b) => (a.edge_node_id || '').localeCompare(b.edge_node_id || '')).map(node => {
              // Calculate online status from heartbeat (UTC-safe, 15 min threshold)
              const isOnline = isNodeOnline(node);
              const status = isOnline ? 'Online' : 'Offline';
              return (
                <button key={node.edge_node_id} onClick={() => { onSelect(node.edge_node_id); setIsOpen(false); }} className={`w-full px-4 py-3 text-left hover:bg-slate-700/50 ${selectedAgent === node.edge_node_id ? 'bg-cyan-500/20' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <span className={`text-sm font-medium ${selectedAgent === node.edge_node_id ? 'text-cyan-300' : 'text-white'}`}>{edgeNodeDisplayName(node)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ml-auto ${isOnline ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR WITH BRIEFING BUTTON
// ════════════════════════════════════════════════════════════════════════════

// Alerts and findings come from utils/counts.js over the window the chip states.
const StatsBar = ({ stats, counts, onOpenPanel, onRequestBriefing }) => {
  const threatsCount = counts?.findings || 0;
  const alertCount = counts?.alerts || 0;
  return (
  <div className="flex items-center gap-4">
    {/* On-demand Briefing Button */}
    <button
      onClick={onRequestBriefing}
      className="flex items-center gap-2 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 hover:from-cyan-600/30 hover:to-blue-600/30 border border-cyan-500/50 rounded-lg px-4 py-2 transition-all"
    >
      <span className="text-lg">📋</span>
      <span className="text-sm font-medium text-cyan-300">Briefing</span>
    </button>
    
    <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
      <span className="text-emerald-300 font-bold text-lg">{stats.onlineAgents || 0}</span>
      <span className="text-slate-300 text-xs">/ {stats.totalAgents || 0} Agents Online</span>
    </div>
    <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
      <span className="text-blue-300 font-bold text-lg">{stats.totalDevices || 0}</span>
      <span className="text-slate-300 text-xs">Assets</span>
    </div>
    {alertCount > 0 && (
      <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
        <span data-testid="count-alerts" className="text-red-300 font-bold text-lg">{alertCount}</span>
        <span className="text-red-300/70 text-xs">Alerts</span>
      </div>
    )}
    {counts?.window && (
      <div data-testid="count-window" className="text-[10px] text-slate-400 leading-tight max-w-[150px]">{counts.window.label}</div>
    )}
    <button onClick={onOpenPanel} className={`flex items-center gap-2 rounded-lg px-4 py-2 border transition-all ${threatsCount > 0 ? 'bg-purple-500/20 border-purple-400/50 hover:bg-purple-500/30' : 'bg-emerald-500/20 border-emerald-400/50 hover:bg-emerald-500/30'}`}>
      <span className="text-lg">🤖</span>
      <div className="text-left">
        <div className={`font-bold text-sm ${threatsCount > 0 ? 'text-purple-300' : 'text-emerald-300'}`}>EdgeNotary</div>
        <div className={`text-[10px] ${threatsCount > 0 ? 'text-purple-300/70' : 'text-emerald-300/70'}`}>{threatsCount > 0 ? `${threatsCount} detections` : 'All clear'}</div>
      </div>
      {threatsCount > 0 && (
        <span data-testid="count-findings-badge" className="w-5 h-5 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">{threatsCount}</span>
      )}
    </button>
  </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS_ROWS = [
  { key: 'ISOLATED',  icon: '🛡️', label: 'Isolated',  color: 'text-red-300' },
  { key: 'CRITICAL',  icon: '🚨', label: 'Critical',  color: 'text-red-300' },
  { key: 'ANALYZING', icon: '🔍', label: 'Analyzing', color: 'text-purple-300' },
  { key: 'ALERT',     icon: '⚠️', label: 'Alert',     color: 'text-yellow-300' },
  { key: 'PROTECTED', icon: '🤖', label: 'Monitored', color: 'text-emerald-300' },
];

const Legend = ({ threats = [], nodes = [] }) => {
  const presentStatuses = new Set(
    nodes.filter(n => n.data?.aiStatus).map(n => n.data.aiStatus.key)
  );
  const visibleRows = AI_STATUS_ROWS.filter(r => presentStatuses.has(r.key) || presentStatuses.size === 0);
  return (
    <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
      <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
      <div className="space-y-2">
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-400" /><span className="text-xs text-slate-300">Online</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-400" /><span className="text-xs text-slate-300">Offline / Alert</span></div>
      </div>
      <div className="pt-2 border-t border-slate-600/50">
        <div className="text-[10px] font-semibold text-slate-400 uppercase mb-2">AI Status</div>
        <div className="space-y-2">
          {visibleRows.map(r => (
            <div key={r.key} className="flex items-center gap-2">
              <span className="text-xs">{r.icon}</span>
              <span className={`text-xs ${r.color}`}>{r.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="pt-2 border-t border-slate-600/50">
        <button
          onClick={() => window.location.href = '/ai-sentry-light'}
          className="flex items-center gap-1.5 w-full hover:bg-slate-700/50 rounded-lg px-1 py-1.5 transition-colors"
          title="Switch to Light Mode"
        >
          <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm0 15a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm9-6a1 1 0 110 2h-1a1 1 0 110-2h1zM4 12a1 1 0 110 2H3a1 1 0 110-2h1zm14.95-7.364a1 1 0 010 1.414l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 0zM7.172 16.828a1 1 0 010 1.414l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 0zm9.9 0a1 1 0 011.414 0l.707.707a1 1 0 01-1.414 1.414l-.707-.707a1 1 0 010-1.414zM5.636 5.636a1 1 0 011.414 0l.707.707A1 1 0 016.343 7.757l-.707-.707a1 1 0 010-1.414zM12 7a5 5 0 100 10A5 5 0 0012 7z" />
          </svg>
          <div className="w-8 h-4 bg-slate-600 rounded-full relative mx-0.5">
            <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-slate-400 rounded-full shadow-sm" />
          </div>
          <svg className="w-3.5 h-3.5 text-slate-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const AISentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edgeNodesList, setEdgeNodesList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [threats, setThreats] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);
  const [aiBriefing, setAiBriefing] = useState(null);
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({ totalAgents: 0, onlineAgents: 0, totalDevices: 0, alertCount: 0 });
  const [assetsByIp, setAssetsByIp] = useState({});

  // Build topology
  const buildTopology = useCallback((edgeNodes, events, filterAgentId, currentThreats, assetsByIp = {}) => {
    const newNodes = [];
    const newEdges = [];
    const filteredEdgeNodes = filterAgentId ? edgeNodes.filter(n => n.edge_node_id === filterAgentId) : edgeNodes;
    
    const CLOUD_Y = 0, EDGE_Y = 200, DEVICE_Y_START = 420;
    const HORIZONTAL_GAP = filteredEdgeNodes.length > 4 ? 260 : filteredEdgeNodes.length > 3 ? 300 : 350;
    const DEVICE_GAP = 180;
    
    const devicesByAgent = {};
    
    events.forEach(event => {
      const agentId = event.celerway_edge_node_id;
      const destIp = event.destination_ip;
      if (!agentId || !destIp || destIp === 'N/A') return;
      if (filterAgentId && agentId !== filterAgentId) return;
      
      if (!devicesByAgent[agentId]) devicesByAgent[agentId] = {};
      if (!devicesByAgent[agentId][destIp]) {
        const asset = assetsByIp[destIp];
        const known = KNOWN_DEVICES[destIp];
        const detailsRole = event.details?.dest_role || event.details?.asset_role;
        devicesByAgent[agentId][destIp] = {
          ip: destIp,
          protocols: new Set(),
          eventCount: 0,
          policyViolations: 0,
          role:   asset?.asset_type  || detailsRole  || known?.role   || 'Device',
          name:   asset?.asset_name  || knownDeviceName(destIp, known?.name) || null,
          vendor: asset?.vendor      || known?.vendor  || null,
        };
      }
      
      const device = devicesByAgent[agentId][destIp];
      device.eventCount++;
      if (event.protocol) device.protocols.add(event.protocol.toLowerCase());
      if (isAlert(event)) device.policyViolations++;
    });

    // Drop IPs that are neither in KNOWN_DEVICES nor registered in the assets table.
    // This filters management-plane IPs and agent self-traffic while keeping
    // both local Pi devices (KNOWN_DEVICES) and cloud fleet devices (assetsByIp).
    Object.keys(devicesByAgent).forEach(agentId => {
      Object.keys(devicesByAgent[agentId]).forEach(ip => {
        if (!KNOWN_DEVICES[ip] && !assetsByIp[ip]) delete devicesByAgent[agentId][ip];
      });
    });

    // Ensure all registered assets for each agent appear in the topology even if
    // they have no events yet in the current window.
    filteredEdgeNodes.forEach(node => {
      const agentId = node.edge_node_id;
      if (!devicesByAgent[agentId]) devicesByAgent[agentId] = {};
      // Seed from assets table (fleet / cloud agents)
      Object.values(assetsByIp)
        .filter(a => a.celerway_edge_node_id === agentId)
        .forEach(asset => {
          const ip = asset.ip_address;
          if (ip && !devicesByAgent[agentId][ip]) {
            devicesByAgent[agentId][ip] = {
              ip,
              protocols: new Set(),
              eventCount: 0,
              policyViolations: 0,
              role:   asset.asset_type || 'Device',
              name:   asset.asset_name || ip,
              vendor: asset.vendor     || '',
            };
          }
        });
      // Seed KNOWN_DEVICES, but only for /24 subnets this agent has already seen.
      // Using all subnets would inject every fleet site's devices into every agent.
      const observedPrefixes = new Set(
        Object.keys(devicesByAgent[agentId]).map(ip => ip.split('.').slice(0,3).join('.'))
      );
      if (observedPrefixes.size > 0) {
        Object.entries(KNOWN_DEVICES).forEach(([ip, known]) => {
          const prefix = ip.split('.').slice(0,3).join('.');
          if (observedPrefixes.has(prefix) && !devicesByAgent[agentId][ip]) {
            const asset = assetsByIp[ip];
            devicesByAgent[agentId][ip] = {
              ip,
              protocols: new Set(),
              eventCount: 0,
              policyViolations: 0,
              role:   asset?.asset_type || known.role,
              name:   asset?.asset_name || knownDeviceName(ip, known.name),
              vendor: asset?.vendor     || known.vendor,
            };
          }
        });
      }
    });

    // Display-only device type (constants/deviceTypes.js): the stored role (asset_type / dest_role,
    // which policies match on) is not changed; only the badge label and its style class are.
    Object.values(devicesByAgent).forEach(devices => Object.values(devices).forEach(d => {
      d.typeLabel = deviceTypeLabel(d.name, d.vendor, d.role);
      d.role = deviceRoleClass(d.name, d.vendor, d.role);
    }));

    let totalDevices = 0;
    Object.values(devicesByAgent).forEach(d => totalDevices += Object.keys(d).length);

    const edgeStartX = 400 - ((filteredEdgeNodes.length - 1) * HORIZONTAL_GAP) / 2;
    const centerX = edgeStartX + ((filteredEdgeNodes.length - 1) * HORIZONTAL_GAP) / 2;
    
    newNodes.push({
      id: 'cloud', type: 'cloud', position: { x: centerX, y: CLOUD_Y },
      data: { label: 'EdgeNotary Cloud', sublabel: 'EdgeNotary Management', edgeCount: filteredEdgeNodes.length, assetCount: totalDevices },
    });
    
    filteredEdgeNodes.forEach((node, idx) => {
      const agentDevices = devicesByAgent[node.edge_node_id] || {};
      const deviceList = Object.values(agentDevices);
      const nodeX = edgeStartX + idx * HORIZONTAL_GAP;
      const status = getNodeStatus(node);
      
      newNodes.push({
        id: `edge-${node.edge_node_id}`, type: 'edgeGateway', position: { x: nodeX, y: EDGE_Y },
        data: { nodeId: node.edge_node_id, status, deviceCount: deviceList.length, displayName: edgeNodeDisplayName(node) },
      });
      
      newEdges.push({
        id: `cloud-to-edge-${node.edge_node_id}`, source: 'cloud', target: `edge-${node.edge_node_id}`,
        type: 'smoothstep', animated: true, style: { stroke: status === 'Online' ? '#34d399' : '#f87171', strokeWidth: 2 },
      });
      
      const deviceStartX = nodeX - ((deviceList.length - 1) * DEVICE_GAP) / 2;
      
      deviceList.forEach((device, deviceIdx) => {
        const deviceId = `device-${node.edge_node_id}-${device.ip.replace(/\./g, '-')}`;
        const deviceAiStatus = getDeviceAIStatus(device.ip, currentThreats);
        
        newNodes.push({
          id: deviceId, type: 'device',
          position: { x: deviceStartX + deviceIdx * DEVICE_GAP, y: DEVICE_Y_START + (deviceIdx % 2) * 60 },
          data: { ip: device.ip, name: device.name, role: device.role, typeLabel: device.typeLabel, vendor: device.vendor, protocols: Array.from(device.protocols), eventCount: device.eventCount, policyViolations: device.policyViolations, aiStatus: deviceAiStatus },
        });
        
        let edgeColor = device.policyViolations > 0 ? '#f87171' : device.protocols.has('modbus') ? '#fb923c' : '#22d3ee';
        if (isRedStatus(deviceAiStatus)) edgeColor = '#ef4444';
        else if (deviceAiStatus === AI_STATUS.ANALYZING) edgeColor = '#a855f7';
        
        newEdges.push({
          id: `edge-${node.edge_node_id}-to-${deviceId}`, source: `edge-${node.edge_node_id}`, target: deviceId,
          type: 'smoothstep', animated: true, style: { stroke: edgeColor, strokeWidth: deviceAiStatus !== AI_STATUS.PROTECTED ? 3 : 2 },
        });
      });
    });
    
    return { 
      nodes: newNodes, edges: newEdges, 
      stats: { totalAgents: filteredEdgeNodes.length, onlineAgents: filteredEdgeNodes.filter(n => isNodeOnline(n)).length, totalDevices }
    };
  }, []);

  // Fetch AI Briefing — scoped to selectedAgent
  const fetchBriefing = useCallback(async () => {
    setIsLoadingAI(true);
    const siteEvents = selectedAgent
      ? allEvents.filter(e => e.celerway_edge_node_id === selectedAgent)
      : allEvents;
    // The same counts the Findings tile shows (computeCounts over the page's events and threats).
    const briefing = await generateAIBriefing(selectedAgent, siteEvents, computeCounts({ events: allEvents, threats, siteId: selectedAgent }));
    setAiBriefing(briefing);
    setIsLoadingAI(false);
  }, [selectedAgent, allEvents, threats]);

  // Fetch Expanded Briefing — scoped to selectedAgent
  const fetchExpandedBriefing = useCallback(async () => {
    setIsLoadingExpanded(true);
    const siteEvents = selectedAgent
      ? allEvents.filter(e => e.celerway_edge_node_id === selectedAgent)
      : allEvents;
    const expanded = await generateExpandedBriefing(selectedAgent, siteEvents, computeCounts({ events: allEvents, threats, siteId: selectedAgent }));
    setExpandedBriefing(expanded);
    setIsLoadingExpanded(false);
  }, [selectedAgent, allEvents, threats]);

  // Request on-demand briefing
  const handleRequestBriefing = useCallback(() => {
    setShowBriefing(true);
    setAiBriefing(null);
    setExpandedBriefing(null);
    fetchBriefing();
  }, [fetchBriefing]);

  // Manual refresh for panel
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadData();
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent]);

  // Handle VPN profile change approval - calls Nimbus API via backend
  const handleApproveVPN = useCallback(async (action) => {
    console.log('🌐 Executing VPN change:', action);
    
    try {
      const result = await executeNimbusAction('vpn_profile_change', {
        agent_id: action.agentId || 'Stratus5G_5728_Austin',
        current_profile: action.actionDetails?.currentProfile || 'Standard',
        new_profile: action.actionDetails?.recommendedProfile || 'Isolated-Secure',
        threat_source: action.device,
        target_devices: action.targetDevices,
        reason: action.reason
      });
      
      console.log('🌐 VPN change result:', result);
      return result;
    } catch (error) {
      console.error('🌐 VPN change failed:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // One fetch feeds every count on the page (utils/counts.js): the selected site's most recent
  // EVENT_SAMPLE_LIMIT events, fetched site-scoped so another site's traffic cannot take a share of
  // the limit. "All Edge Nodes" (null) fetches across sites. The first node is selected on first
  // load (or when the selected node disappears) and never again, so "All Edge Nodes" sticks.
  const defaultSelectedRef = useRef(false);
  const requestRef = useRef(0);
  const loadData = async () => {
    const request = ++requestRef.current;
    const edgeNodesResponse = await edgeNodesService.getEdgeNodes();
    const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
    const valid = !!selectedAgent && edgeNodes.some(n => n.edge_node_id === selectedAgent);
    const needsDefault = edgeNodes.length > 0 && !valid && (!defaultSelectedRef.current || !!selectedAgent);
    defaultSelectedRef.current = true;
    if (request !== requestRef.current) return 'stale';
    setEdgeNodesList(edgeNodes);
    if (needsDefault) {
      setSelectedAgent(edgeNodes[0].edge_node_id);
      return 'reselected'; // the effect re-runs fetchData for the selected site
    }
    const agentToUse = valid ? selectedAgent : null;

    const [eventsResponse, assetsResponse] = await Promise.all([
      eventsService.getEvents({ limit: EVENT_SAMPLE_LIMIT, edge_node_id: agentToUse }),
      assetsService.getAssets({ limit: 500 }).catch(() => []),
    ]);
    if (request !== requestRef.current) return 'stale';

    const events = eventsResponse.data || eventsResponse || [];
    const assets = Array.isArray(assetsResponse) ? assetsResponse : (assetsResponse?.data || []);
    const newAssetsByIp = {};
    assets.forEach(a => { if (a.ip_address) newAssetsByIp[a.ip_address] = a; });

    setAllEvents(events);
    setAssetsByIp(newAssetsByIp);

    const newThreats = detectThreatsHeuristic(events);
    setThreats(newThreats);

    const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, agentToUse, newThreats, newAssetsByIp);
    setNodes(newNodes);
    setEdges(newEdges);
    setStats(newStats);
    return 'loaded';
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    let outcome = null;
    try {
      if (!defaultSelectedRef.current) setLoading(true);
      outcome = await loadData();
    } catch (err) {
      console.error('Failed to fetch:', err);
      setError(err.message);
    } finally {
      if (outcome !== 'reselected' && outcome !== 'stale') setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildTopology, setNodes, setEdges, selectedAgent]);

  // Every count on the page, for the selected site, over the one fetched window.
  const counts = useMemo(
    () => computeCounts({ events: allEvents, threats, siteId: selectedAgent }),
    [allEvents, threats, selectedAgent]
  );
  const siteNode = edgeNodesList.find(n => n.edge_node_id === selectedAgent);
  const siteName = siteNode ? edgeNodeDisplayName(siteNode) : 'All sites';

  useEffect(() => {
    if (edgeNodesList.length > 0) {
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodesList, allEvents, selectedAgent, threats, assetsByIp);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    }
  }, [selectedAgent, edgeNodesList, allEvents, threats, assetsByIp, buildTopology, setNodes, setEdges]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="h-screen w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Back arrow */}
            <a
              href="/dashboard"
              className="mt-1 flex items-center justify-center w-8 h-8 rounded-lg bg-slate-800/70 border border-slate-700/50 text-slate-400 hover:text-white hover:border-cyan-500/50 transition-colors flex-shrink-0"
              title="Back to main"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </a>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-400/30">
                  <span className="text-xl">🤖</span>
                </span>
                EdgeNotary
              </h1>
              <p className="text-slate-300 text-sm mt-1">Real-time OT Analysis • Policy-Driven Response • Proactive Protection</p>
              <div className="mt-3">
                <EdgeNodeFilter edgeNodes={edgeNodesList} selectedAgent={selectedAgent} onSelect={setSelectedAgent} />
              </div>
            </div>
          </div>
          <StatsBar stats={stats} counts={counts} onOpenPanel={() => setIsAIPanelOpen(true)} onRequestBriefing={handleRequestBriefing} />
        </div>
      </div>
      
      {/* Shift Briefing Modal */}
      <ShiftBriefingModal 
        isOpen={showBriefing && !loading}
        onClose={() => setShowBriefing(false)}
        aiBriefing={aiBriefing}
        stats={stats}
        threats={siteThreats(threats, selectedAgent)}
        counts={counts}
        siteName={siteName}
        isLoadingAI={isLoadingAI}
        onRequestExpanded={fetchExpandedBriefing}
        expandedBriefing={expandedBriefing}
        isLoadingExpanded={isLoadingExpanded}
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">EdgeNotary Initializing...</div>
          </div>
        </div>
      )}
      
      {/* Error */}
      {error && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="bg-red-500/20 border border-red-400/50 rounded-2xl p-8 text-center">
            <div className="text-red-300 text-lg">Failed to load: {error}</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && !error && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.4 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(6, 182, 212, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'cloud') return '#22d3ee';
              if (node.type === 'edgeGateway') return node.data?.status === 'Online' ? '#34d399' : '#f87171';
              if (isRedStatus(node.data?.aiStatus)) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.ANALYZING) return '#a855f7';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend threats={threats} nodes={nodes} /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        threats={siteThreats(threats, selectedAgent)}
        recentEvents={siteEvents(allEvents, selectedAgent)}
        counts={counts}
        edgeNodes={edgeNodesList}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        onApproveVPN={handleApproveVPN}
        selectedAgent={selectedAgent}
      />
      
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

// Named exports for component tests (monitor-mode wording).
export { AI_STATUS, detectThreatsHeuristic, getDeviceAIStatus, generateAIBriefing, generateExpandedBriefing, DeviceNode, ShiftBriefingModal, AIPanel, Legend };
export default AISentry;
